import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  filter,
  map,
  mergeMap,
  Observable,
  race,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Option } from '@sapphire/result';

import { ToastService } from '@ps/core-overlay';
import {
  countryChangeLossMessages,
  type LocaleChangeTargeting,
} from '@ps/survey-utils';
import { fromOption } from '@ps/utils';

import {
  selectSurvey,
  selectSurveyId,
} from '../core/survey-creation-core.selectors';
import { SurveyCreationReferenceApiActions } from '../reference/survey-creation-reference.actions';
import {
  selectQualificationsCatalogRecord,
  selectQuestionsRecord,
} from '../reference/survey-creation-reference.selectors';
import { SurveyCreationAudienceActions } from './survey-creation-audience.actions';
import {
  selectMetadataForm,
  selectQualificationsForm,
  selectQuotasForm,
} from './survey-creation-audience.selectors';
import {
  eligibleSurveyForLocaleChange,
  isLocaleCatalogLoadFailure,
  type LocaleCatalogs,
  type LocaleChangeRequest,
  loadedLocaleCatalogs,
  localeCatalogLoads,
  localeChangeTargetingFor,
  replacedSurveyId,
  toLocaleChangeRequest,
} from './utils/created-survey-locale-change.helper';

@Injectable()
export class SurveyCreationLocaleChangeEffects {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _toastService = inject(ToastService);

  public readonly loadQualificationRecords$ = createEffect(() =>
    this._localeChangeRequests$().pipe(
      mergeMap((request) => localeCatalogLoads(request)),
    ),
  );

  public readonly createSurveyAfterLocaleChange$ = createEffect(() =>
    this._localeChangeRequests$().pipe(
      switchMap((request) =>
        this._retainedTargeting$(request).pipe(
          tap((retained) => this._toastLosses(retained)),
          map((targeting) =>
            SurveyCreationAudienceActions.createSurveyAfterLocaleChange({
              locale: request.locale,
              targeting,
              deleteSurveyId: replacedSurveyId(request),
            }),
          ),
        ),
      ),
    ),
  );

  private _localeChangeRequests$(): Observable<LocaleChangeRequest> {
    return this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.changeCreatedSurveyLocale),
      concatLatestFrom(() => [
        this._store.select(selectSurvey),
        this._store.select(selectSurveyId),
        this._store.select(selectMetadataForm),
        this._store.select(selectQualificationsForm),
        this._store.select(selectQuotasForm),
      ]),
      mergeMap(
        ([
          trigger,
          survey,
          surveyId,
          metadataForm,
          qualificationsForm,
          quotasForm,
        ]) =>
          fromOption(
            eligibleSurveyForLocaleChange(survey, surveyId).map(
              (eligibleSurvey) =>
                toLocaleChangeRequest(trigger, {
                  survey: eligibleSurvey,
                  metadata: Option.from(metadataForm.value),
                  qualifications: qualificationsForm.value,
                  quotas: quotasForm.value,
                }),
            ),
          ),
      ),
    );
  }

  // Next locale's catalogs, or abandon on a load failure — whichever comes first.
  private _retainedTargeting$(
    request: LocaleChangeRequest,
  ): Observable<LocaleChangeTargeting> {
    return race(
      this._loadedCatalogs$(request).pipe(map(Option.some)),
      this._catalogLoadFailure$(request).pipe(map(() => Option.none)),
    ).pipe(
      mergeMap((catalogs) => fromOption(catalogs)),
      map((catalogs) => localeChangeTargetingFor(request, catalogs)),
    );
  }

  // Reads the store, not success actions, so a cached (skipped) load still resolves.
  private _loadedCatalogs$(
    request: LocaleChangeRequest,
  ): Observable<LocaleCatalogs> {
    return combineLatest([
      this._store.select(selectQualificationsCatalogRecord),
      this._store.select(selectQuestionsRecord),
    ]).pipe(
      mergeMap(([catalogRecord, questionsRecord]) =>
        fromOption(
          loadedLocaleCatalogs(request, catalogRecord, questionsRecord),
        ),
      ),
      take(1),
    );
  }

  private _catalogLoadFailure$(request: LocaleChangeRequest): Observable<void> {
    return this._actions$.pipe(
      ofType(
        SurveyCreationReferenceApiActions.loadQualificationsFailure,
        SurveyCreationReferenceApiActions.loadQuestionsFailure,
      ),
      filter((failure) => isLocaleCatalogLoadFailure(request, failure)),
      take(1),
      map(() => undefined),
    );
  }

  private _toastLosses(retained: LocaleChangeTargeting): void {
    for (const message of countryChangeLossMessages(retained)) {
      this._toastService.warning(message, { keepOpen: false });
    }
  }
}
